import React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IFullscreenPreviewComment } from '~/client/src/shared/components/FileFullscreenPreview/FileFullscreenPreview.store'
import DeliveryStatus from '~/client/src/shared/constants/DeliveryStatus'
import FileType from '~/client/src/shared/enums/FileType'
import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BaseNotification from '~/client/src/shared/models/Notification'
import Photo from '~/client/src/shared/models/Photo'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import DeliveryStatusChangesStore from '~/client/src/shared/stores/domain/DeliveryStatusChanges.store'
import GatesStore from '~/client/src/shared/stores/domain/Gates.store'
import MessagesStore from '~/client/src/shared/stores/domain/MessagesStore/Messages.store'
import PhotosStore from '~/client/src/shared/stores/domain/Photos.store'
import ZonesStore from '~/client/src/shared/stores/domain/Zones.store'
import { SHOW_FULLSCREEN_PREVIEW } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import {
  isDeleteType,
  isDeliveryType,
} from '~/client/src/shared/types/NotificationTypes'
import { NO_IMAGE_PATH } from '~/client/src/shared/utils/usefulStrings'

const MAX_PHOTOS_COUNT = 5

// translated

interface IProps {
  notification: BaseNotification
  deliveriesStore?: DeliveriesStore
  gatesStore?: GatesStore
  zonesStore?: ZonesStore
  messagesStore?: MessagesStore
  photosStore?: PhotosStore
  deliveryStatusChangesStore?: DeliveryStatusChangesStore
  eventsStore?: EventsStore
  companiesStore?: CompaniesStore
}

@inject(
  'gatesStore',
  'zonesStore',
  'eventsStore',
  'deliveriesStore',
  'messagesStore',
  'photosStore',
  'deliveryStatusChangesStore',
  'companiesStore',
)
@observer
export default class NotificationPhotos extends React.Component<IProps> {
  @observable public areImagesLoaded: boolean = false

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const {
      notification,
      deliveriesStore,
      zonesStore,
      gatesStore,
      companiesStore,
    } = this.props
    const { type, entityId } = notification
    let photos: Photo[] = []
    let deliveryStatus: DeliveryStatus = null
    let companyName: string = ''
    const photoDetails: string[] = []

    switch (true) {
      case isDeleteType(type):
        return <div />
      case isDeliveryType(type):
        const delivery = deliveriesStore.list.find(d => d.id === entityId)
        if (!delivery) {
          break
        }
        photos = this.getDeliveryPhotos()
        const { company, status, zone, gate } = delivery
        companyName = companiesStore.getCompanyNameById(company)
        deliveryStatus = status
        photoDetails.push(zonesStore.getString(zone, false))
        photoDetails.push(gatesStore.getString(gate, false))
        break
      default:
        return null
    }

    return this.renderPhotosRow(
      photos,
      deliveryStatus,
      companyName,
      photoDetails,
    )
  }

  private renderPhotosRow(
    allPhotos: Photo[],
    deliveryStatus: DeliveryStatus,
    companyName: string,
    photoDetails: string[],
  ) {
    const photos = allPhotos.slice(allPhotos.length - MAX_PHOTOS_COUNT)
    return (
      <div className="row">
        <div className="row reverse">
          {photos.map((photo, index) => {
            return (
              <img
                className={classList({
                  'photo-container': index !== photos.length - 1,
                  'photo-container-first': index === photos.length - 1,
                  'not-visible': !this.areImagesLoaded,
                })}
                onClick={this.openPhoto.bind(
                  this,
                  allPhotos,
                  photo.url,
                  companyName,
                  photoDetails,
                  deliveryStatus,
                )}
                key={photo.id || index}
                src={photo.url || NO_IMAGE_PATH}
                onLoad={this.setLoaded}
              />
            )
          })}
        </div>
        {allPhotos.length > MAX_PHOTOS_COUNT && (
          <div className="no-grow text vertical large">
            {Localization.translator.plusXMore(
              allPhotos.length - MAX_PHOTOS_COUNT,
            )}
          </div>
        )}
      </div>
    )
  }

  private getDeliveryPhotos() {
    const {
      notification,
      deliveriesStore,
      deliveryStatusChangesStore,
      messagesStore,
      photosStore,
    } = this.props
    const { id, threadId } = deliveriesStore.list.find(
      d => d.id === notification.entityId,
    )
    const deliveryThreadsIds = deliveryStatusChangesStore
      .getStatusChangesForDelivery(id)
      .map(status => status.threadId)
      .concat(threadId)
    const messagesIds = messagesStore.list
      .filter(m => deliveryThreadsIds.includes(m.threadId))
      .map(m => m.id)

    return photosStore.list.filter(p => messagesIds.includes(p.messageId))
  }

  @action.bound
  private openPhoto(
    photos: Photo[],
    selectedPhotoUrl: string,
    companyName: string,
    photoDetails: string[],
    deliveryStatus: DeliveryStatus,

    event: React.SyntheticEvent<HTMLDivElement>,
  ) {
    const { messagesStore } = this.props
    const reversedPhotos = photos.reverse()
    const messagesIds = photos.map(p => p.messageId)
    const messages = messagesStore.list.filter(m => messagesIds.includes(m.id))
    const comments: IFullscreenPreviewComment[] = []
    const files: IFilePreviewProperties[] = []

    messages.forEach(message => {
      const { photoId, userId, text, createdAt } = message
      const photo = reversedPhotos.find(p => p.id === photoId)
      files.push({ fileUrl: photo.url, fileType: FileType.Image })
      comments.push({
        authorId: userId,
        text,
        deliveryStatus,
        companyName,
        timestamp: createdAt,
        photoDetails,
      })
    })

    const currentFileIndex = files.findIndex(
      photo => photo.fileUrl === selectedPhotoUrl,
    )
    event.stopPropagation()
    this.props.eventsStore.dispatch(
      SHOW_FULLSCREEN_PREVIEW,
      files,
      currentFileIndex,
      comments,
    )
  }

  @action.bound
  private setLoaded() {
    this.areImagesLoaded = true
  }
}
